import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { graphql } from '@lib/api';
import { AGENT_BY_ID_QUERY } from '@lib/fragments';
import { useAxios } from 'use-axios-client';
import { useRouter } from 'next/router';

// ---------------------------------------------------------

import Modal from '@components/modal/component';
import StepOne from './step-one/component';
import StepTwo from './step-two/component';
import StepThree from './step-three/component';
// ---------------------------------------------------------

import styles from './styles.module.scss';
const { container } = styles;

// ---------------------------------------------------------

const RequestATourModal = (props) => {
  const {
    agentId,
    modalIsOpen,
    setModalIsOpen,
    userInfo,
    address,
    zip,
    city,
    state,
    photos,
    listingId,
    listingUrl
  } = props;

  const router = useRouter();

  const [currentStep, setCurrentStep] = useState(1);
  const [availability, setAvailability] = useState('Next Available');
  const [date, setDate] = useState('false');
  const [time, setTime] = useState('12:00 PM');
  const [details, setDetails] = useState('');
  const [selected, setSelected] = useState([]);
  const [fullName, setFullName] = useState('');
  const [emailRequired, setEmailRequired] = useState(false);
  const [phoneRequired, setPhoneRequired] = useState(false);
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [agent, setAgent] = useState();
  const [associatedAgentId, setAssociatedAgentId] = useState(0);
  const [loggedInUserId, setLoggedInUserId] = useState('');
  const [checkboxError, setCheckboxError] = useState(false);
  const [dateError, setDateError] = useState(false);

  const { data, refetch } = useAxios(`/api/user/account`);

  useEffect(() => {
    if (router.query.tourModal) {
      setModalIsOpen(true);
    }

    const storedModalData = JSON.parse(localStorage.getItem('request a tour'));
    if (new Date(storedModalData?.expiration) <= new Date()) {
      localStorage.removeItem('request a tour');
      return;
    }
    if (new Date(storedModalData?.date) >= new Date()) setDate(new Date(storedModalData.date));
    if (storedModalData?.associatedAgent) setAgent(storedModalData.associatedAgent);
    if (storedModalData?.availability) setAvailability(storedModalData.availability);
    if (storedModalData?.email) setEmail(storedModalData.email);
    if (storedModalData?.details) setDetails(storedModalData.details);
    if (storedModalData?.phoneNumber) setPhoneNumber(storedModalData.phoneNumber);
    if (storedModalData?.time) setTime(storedModalData.time);
    if (storedModalData?.fullName) setFullName(storedModalData.fullName);
    if (storedModalData?.selected) setSelected(storedModalData.selected);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!data?.userId) return;
      setLoggedInUserId(data.userId);

      if (!data?.associatedAgent) return;

      let variables = {
        agentId: data.associatedAgent
      };

      setAssociatedAgentId(data.associatedAgent);

      const fetchedData = await graphql(
        gql`
          ${AGENT_BY_ID_QUERY}
        `,
        variables
      );

      setAgent(fetchedData?.agentSearch?.items[0] ?? []);
    };

    fetchData();
  }, [data?.associatedAgent, refetch]);

  useEffect(() => {
    if (selected?.length > 0) {
      const isEmail = selected.filter((el) => {
        return el === 'Email';
      });

      const isPhone = selected.filter((el) => {
        return el === 'Text' || el === 'Call';
      });

      isEmail.length > 0 ? setEmailRequired(true) : setEmailRequired(false);
      isPhone.length > 0 ? setPhoneRequired(true) : setPhoneRequired(false);
      setCheckboxError(false);
    }

    if (date !== 'false') {
      setDateError(false);
    }
  }, [selected, details, date]);

  const onModalClose = () => {
    setModalIsOpen(false);
    setCurrentStep(1);

    router.replace(listingUrl, undefined, { shallow: true });
  };
  return (
    <Modal
      modalIsOpen={modalIsOpen}
      onModalClose={onModalClose}
      content={
        <div className={container}>
          {currentStep == 1 && (
            <StepOne
              nextStepCallback={(step) => setCurrentStep(step)}
              currentStep={currentStep}
              onChange={setAvailability}
              availability={availability}
              dateCallback={(el) => setDate(el)}
              date={date}
              dateError={dateError}
              dateErrorCallback={setDateError}
              setTime={setTime}
              time={time}
              setDetails={setDetails}
              details={details}
              setSelected={setSelected}
              selected={selected}
              checkboxError={checkboxError}
              checkboxErrorCallback={setCheckboxError}
            ></StepOne>
          )}
          {currentStep == 2 && (
            <StepTwo
              details={details}
              nextStepCallback={(step) => setCurrentStep(step)}
              currentStep={currentStep}
              userInfo={userInfo}
              address={address}
              time={time}
              date={date}
              availability={availability}
              setModalIsOpen={setModalIsOpen}
              fullNameCallback={setFullName}
              fullName={fullName}
              emailRequired={emailRequired}
              phoneRequired={phoneRequired}
              email={email}
              emailCallback={setEmail}
              phoneNumber={phoneNumber}
              phoneNumberCallback={setPhoneNumber}
              agent={agent?.name || agent}
              listingId={listingId}
              loggedInUserId={loggedInUserId}
              associatedAgentId={associatedAgentId}
              agentCallback={setAgent}
              selected={selected}
              agentId={agentId}
            />
          )}
          {currentStep == 3 && (
            <StepThree
              nextStepCallback={(step) => setCurrentStep(step)}
              zip={zip}
              address={address}
              city={city}
              state={state}
              photos={photos}
            />
          )}
        </div>
      }
    />
  );
};

RequestATourModal.propTypes = {
  /**
   * Specifies the listing address
   */
  address: PropTypes.string,

  /**
   * Specifies the id of the LDP agent card
   */
  agentId: PropTypes.number,

  /**
   * Specifies the listing address city
   */
  city: PropTypes.string,

  /**
   * Specifies the default value for the text area
   */
  defaultFormValue: PropTypes.string,

  /**
   * Specifies the request a tour listing ID
   */
  listingId: PropTypes.string,
  /**
   *
   * Specifies the request a tour listing URL
   */
  listingUrl: PropTypes.string,

  /**
   * Determines if the modal state is set to open.
   */
  modalIsOpen: PropTypes.bool,

  /**
   * Specifies the listing address property photos
   */
  photos: PropTypes.array,

  /**
   * Function to toggle modal state.
   */
  setModalIsOpen: PropTypes.func,

  /**
   * Specifies the listing address state
   */
  state: PropTypes.string,

  /**
   * Specifies the logged in user's information
   */
  userInfo: PropTypes.object,
  /**
   * Specifies the listing address zip code
   */
  zip: PropTypes.string
};

export default RequestATourModal;
